import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Loader2, Upload, Building2, User, Image, Award } from 'lucide-react';

const SPECIALIZATIONS = {
  mechatronics: ['mechanical_electrical_assembly', 'maintenance'],
  mechanical: ['mechanical_assembly', 'maintenance', 'mechanical_designer'],
  electrical: ['switchboards', 'machine_line_edge', 'robotics', 'assembly', 'maintenance', 'electrical_designer'],
  software: ['software_development', 'plc_programming', 'robotics_programming', 'support', 'ai_integration']
};

const REGIONS = ['europe', 'asia', 'america', 'south_america', 'africa'];

const CERTIFICATIONS = {
  plc: ['siemens', 'rockwell', 'mitsubishi', 'omron', 'beckhoff', 'schneider'],
  robotics: ['fanuc', 'abb', 'kuka', 'universal_robots', 'yaskawa', 'kawasaki'],
  electrical_sep: ['g1_operation', 'g2_supervision', 'g3_design'],
  safety_bhp: ['basic_bhp', 'periodic_bhp', 'supervisor_bhp', 'first_aid']
};

const RegisterProfessionalPage = () => {
  const { t } = useLanguage();
  const { registerProfessional, login } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [paymentToken, setPaymentToken] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    address: '',
    city: '',
    country: '',
    phone: '',
    website: '',
    description: '',
    specializations: {},
    regions: [],
    entity_type: 'company',
    certifications: {},
    logo_data: null,
    available_for_travel: false
  });

  const [trialToken, setTrialToken] = useState(null);
  
  useEffect(() => {
    // Check for trial token in URL (legacy admin invites)
    const params = new URLSearchParams(window.location.search);
    const trial = params.get('trial');
    if (trial) {
      setTrialToken(trial);
    }
    // Clear any legacy payment token leftover — registration is now FREE for technicians
    localStorage.removeItem('payment_token');
    localStorage.removeItem('registration_type');
  }, [navigate]);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleEntityTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      entity_type: value
    }));
  };

  const handleSpecializationChange = (spec, subSpec, checked) => {
    setFormData(prev => {
      const newSpecs = { ...prev.specializations };
      if (!newSpecs[spec]) {
        newSpecs[spec] = [];
      }
      if (checked) {
        if (!newSpecs[spec].includes(subSpec)) {
          newSpecs[spec] = [...newSpecs[spec], subSpec];
        }
      } else {
        newSpecs[spec] = newSpecs[spec].filter(s => s !== subSpec);
        if (newSpecs[spec].length === 0) {
          delete newSpecs[spec];
        }
      }
      return { ...prev, specializations: newSpecs };
    });
  };

  const handleRegionChange = (region, checked) => {
    setFormData(prev => ({
      ...prev,
      regions: checked 
        ? [...prev.regions, region]
        : prev.regions.filter(r => r !== region)
    }));
  };

  const handleCertificationChange = (category, cert, checked) => {
    setFormData(prev => {
      const newCerts = { ...prev.certifications };
      if (!newCerts[category]) {
        newCerts[category] = [];
      }
      if (checked) {
        if (!newCerts[category].includes(cert)) {
          newCerts[category] = [...newCerts[category], cert];
        }
      } else {
        newCerts[category] = newCerts[category].filter(c => c !== cert);
        if (newCerts[category].length === 0) {
          delete newCerts[category];
        }
      }
      return { ...prev, certifications: newCerts };
    });
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo file too large. Maximum size is 2MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData(prev => ({ ...prev, logo_data: reader.result }));
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (step === 1) {
      setStep(2);
      return;
    }
    
    if (step === 2) {
      setStep(3);
      return;
    }
    
    if (step === 3) {
      setStep(4);
      return;
    }

    if (step === 4) {
      setStep(5);
      return;
    }
    
    if (!acceptedTerms) {
      toast.error(t('terms_required'));
      return;
    }

    // NEW MODEL (Feb 2026): technician registration is FREE — no payment required.

    setLoading(true);
    
    try {
      await registerProfessional({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        trial_token: trialToken || undefined,
        profile: {
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          country: formData.country,
          phone: formData.phone,
          website: formData.website || null,
          description: formData.description || null,
          specializations: formData.specializations,
          regions: formData.regions,
          entity_type: formData.entity_type,
          certifications: formData.certifications,
          available_for_travel: formData.available_for_travel,
          logo_url: formData.logo_data
        }
      });
      
      // Clear payment token after successful registration
      localStorage.removeItem('payment_token');
      localStorage.removeItem('registration_type');
      toast.success(t('common_success'));
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === 'Email already registered') {
        // Falso errore: il primo submit potrebbe essere riuscito. Proviamo a fare login automatico.
        try {
          await login(formData.email, formData.password);
          toast.success(t('common_success'));
          navigate('/dashboard');
          return;
        } catch (_loginErr) {
          // Login fallito → l'email è davvero di qualcun altro
          toast.error(t('auth_email_taken') || detail);
        }
      } else {
        toast.error(detail || t('common_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (false) {
    // Legacy guard: kept here as a placeholder. Free registration is now allowed.
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="register-pro-page">
      <div className="max-w-2xl mx-auto">
        <Card className="border border-slate-200 rounded-md shadow-sm">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-slate-900 rounded-sm flex items-center justify-center">
                <span className="text-orange-500 font-bold text-xl">A</span>
              </div>
            </div>
            <CardTitle 
              className="text-2xl font-semibold text-slate-900"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {t('auth_register_pro_title')}
            </CardTitle>
            
            {/* FREE Registration badge */}
            <div className="mt-4 inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
              {t('register_free_badge')}
            </div>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {[1, 2, 3, 4, 5].map((s) => (
                <div 
                  key={s}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {s}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-2 text-xs text-slate-500">
              <span>Info</span>
              <span>Tipo</span>
              <span>Spec.</span>
              <span>Cert.</span>
              <span>Regioni</span>
            </div>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Step 1: Account & Company Info */}
              {step === 1 && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('auth_name')}</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="rounded-sm"
                      data-testid="pro-name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('auth_email')}</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="rounded-sm"
                      data-testid="pro-email"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('auth_password')}</Label>
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className="rounded-sm"
                      data-testid="pro-password"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="company_name">{t('pro_company_name')}</Label>
                      <Input
                        id="company_name"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        required
                        className="rounded-sm"
                        data-testid="pro-company"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t('pro_phone')}</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="rounded-sm"
                        data-testid="pro-phone"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="address">{t('pro_address')}</Label>
                    <Input
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      className="rounded-sm"
                      data-testid="pro-address"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">{t('pro_city')}</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                        className="rounded-sm"
                        data-testid="pro-city"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="country">{t('pro_country')}</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                        className="rounded-sm"
                        data-testid="pro-country"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="website">{t('pro_website')}</Label>
                    <Input
                      id="website"
                      name="website"
                      type="url"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://"
                      className="rounded-sm"
                      data-testid="pro-website"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('pro_description')}</Label>
                    <Textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={3}
                      className="rounded-sm"
                      data-testid="pro-description"
                    />
                  </div>

                  {/* Logo Upload */}
                  <div className="space-y-2">
                    <Label>{t('pro_logo')}</Label>
                    <div className="flex items-center gap-4">
                      <div 
                        className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center cursor-pointer hover:border-orange-500 transition-colors overflow-hidden"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {logoPreview ? (
                          <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                        ) : (
                          <Image className="h-8 w-8 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <Button 
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          className="rounded-sm"
                          data-testid="upload-logo-btn"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {t('pro_upload_logo')}
                        </Button>
                        <p className="text-xs text-slate-500 mt-1">{t('pro_logo_hint')}</p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Step 2: Entity Type Selection */}
              {step === 2 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('auth_select_entity')}
                  </h3>
                  
                  <RadioGroup 
                    value={formData.entity_type} 
                    onValueChange={handleEntityTypeChange}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <label 
                      htmlFor="freelancer"
                      className={`border rounded-md p-6 cursor-pointer transition-all ${
                        formData.entity_type === 'freelancer' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <RadioGroupItem value="freelancer" id="freelancer" data-testid="entity-freelancer" />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <User className="h-8 w-8 text-orange-500" />
                            <span className="text-lg font-medium">
                              {t('entity_freelancer')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </label>
                    
                    <label 
                      htmlFor="company"
                      className={`border rounded-md p-6 cursor-pointer transition-all ${
                        formData.entity_type === 'company' 
                          ? 'border-orange-500 bg-orange-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <RadioGroupItem value="company" id="company" data-testid="entity-company" />
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Building2 className="h-8 w-8 text-orange-500" />
                            <span className="text-lg font-medium">
                              {t('entity_company')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              )}
              
              {/* Step 3: Specializations */}
              {step === 3 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('pro_specializations')}
                  </h3>
                  
                  {Object.entries(SPECIALIZATIONS).map(([spec, subs]) => (
                    <div key={spec} className="border border-slate-200 rounded-md p-4">
                      <h4 className="font-medium text-slate-900 mb-3">{t(`spec_${spec}`)}</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {subs.map((sub) => (
                          <div key={sub} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${spec}-${sub}`}
                              checked={formData.specializations[spec]?.includes(sub) || false}
                              onCheckedChange={(checked) => handleSpecializationChange(spec, sub, checked)}
                              data-testid={`spec-${spec}-${sub}`}
                            />
                            <Label 
                              htmlFor={`${spec}-${sub}`}
                              className="text-sm text-slate-700 cursor-pointer"
                            >
                              {t(`sub_${sub}`)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Step 4: Certifications */}
              {step === 4 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <Award className="h-5 w-5 text-orange-500" />
                    {t('cert_title')}
                  </h3>
                  
                  {Object.entries(CERTIFICATIONS).map(([category, certs]) => (
                    <div key={category} className="border border-slate-200 rounded-md p-4">
                      <h4 className="font-medium text-slate-900 mb-3">{t(`cert_${category}`)}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {certs.map((cert) => (
                          <div key={cert} className="flex items-center space-x-2">
                            <Checkbox
                              id={`${category}-${cert}`}
                              checked={formData.certifications[category]?.includes(cert) || false}
                              onCheckedChange={(checked) => handleCertificationChange(category, cert, checked)}
                              data-testid={`cert-${category}-${cert}`}
                            />
                            <Label 
                              htmlFor={`${category}-${cert}`}
                              className="text-sm text-slate-700 cursor-pointer"
                            >
                              {t(`cert_${cert}`)}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Step 5: Regions */}
              {step === 5 && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('pro_regions')}
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {REGIONS.map((region) => {
                      const isChecked = formData.regions.includes(region);
                      return (
                        <div 
                          key={region} 
                          className={`border rounded-md p-4 cursor-pointer transition-colors ${
                            isChecked 
                              ? 'border-orange-500 bg-orange-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => handleRegionChange(region, !isChecked)}
                          data-testid={`region-card-${region}`}
                        >
                          <div className="flex items-center space-x-3">
                            <div 
                              className={`h-4 w-4 rounded-sm border flex items-center justify-center shrink-0 ${
                                isChecked ? 'bg-slate-900 border-slate-900' : 'border-slate-300'
                              }`}
                              data-testid={`region-${region}`}
                              data-checked={isChecked}
                            >
                              {isChecked && (
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                              )}
                            </div>
                            <span className="text-slate-900 text-sm">
                              {t(`region_${region}`)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Available for travel (Trasfertista) — increases visibility for B2B searches */}
                  <div
                    className={`border-2 rounded-lg p-5 cursor-pointer transition-all ${
                      formData.available_for_travel
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, available_for_travel: !prev.available_for_travel }))}
                    data-testid="trasfertista-toggle"
                  >
                    <div className="flex items-start gap-4">
                      <div
                        className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center shrink-0 ${
                          formData.available_for_travel ? 'bg-blue-500 border-blue-500' : 'border-slate-300'
                        }`}
                        data-checked={formData.available_for_travel}
                      >
                        {formData.available_for_travel && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-2xl">✈️</span>
                          <h4 className="font-semibold text-slate-900">{t('pro_trasfertista_title')}</h4>
                        </div>
                        <p className="text-sm text-slate-600">{t('pro_trasfertista_subtitle')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Terms Checkbox - Show only on last step */}
              {step === 5 && (
                <div className="flex items-start gap-3 pt-4 border-t border-slate-200">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    data-testid="accept-terms-checkbox"
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-slate-600">
                    {t('terms_accept')}{' '}
                    <Link to="/terms" target="_blank" className="text-orange-500 hover:underline">
                      {t('terms_link')}
                    </Link>
                    {' '}{t('terms_and')}{' '}
                    <Link to="/privacy" target="_blank" className="text-orange-500 hover:underline">
                      {t('privacy_link')}
                    </Link>
                  </label>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                {step > 1 && (
                  <Button 
                    type="button"
                    variant="outline"
                    className="flex-1 rounded-sm"
                    onClick={() => setStep(step - 1)}
                    data-testid="pro-back"
                  >
                    {t('common_back')}
                  </Button>
                )}
                
                <Button 
                  type="submit" 
                  className="flex-1 bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  disabled={loading}
                  data-testid="pro-submit"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : null}
                  {step < 5 ? t('common_next') : t('auth_register_btn')}
                </Button>
              </div>
            </form>
            
            <div className="mt-6 text-center text-sm text-slate-600">
              <span>{t('auth_have_account')} </span>
              <Link 
                to="/login" 
                className="text-orange-500 hover:text-orange-600 font-medium"
              >
                {t('nav_login')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterProfessionalPage;
